import { ArrowDownRightIcon, FilePlus2, Loader2, Undo2, X } from "lucide-react";
import { Input } from "@/components/ui/input.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  generateAIResponseForMentorship,
  useGetMentorshipAIChatHistory,
} from "@/lib/api/ai.ts";
import { useMemo, useRef, useState, useEffect } from "react";
import Markdown from "react-markdown";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";

interface AIConversationProps {
  onClose: () => void;
  mentorshipId: string;
  addToInput: (text: string) => void;
  createNote: (text: string) => void;
}

export function AIConversation({
  onClose,
  addToInput,
  createNote,
  mentorshipId,
}: AIConversationProps) {
  const { data: messages, refetch } =
    useGetMentorshipAIChatHistory(mentorshipId);
  const [userMessage, setUserMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { track } = useTrackEvent();
  const [hasTrackedStart, setHasTrackedStart] = useState(false);

  const messagesContainer = useRef<HTMLDivElement | null>(null);

  const [latestUserMessage, setLatestUserMessage] = useState<string | null>(
    null,
  );
  const [latestAIResponse, setLatestAIResponse] = useState<string | null>(null);

  useEffect(() => {
    if (!hasTrackedStart) {
      track(PostHogEvents.COPILOT_CHAT_STARTED, { mentorship_id: mentorshipId });
      setHasTrackedStart(true);
    }
  }, [hasTrackedStart, mentorshipId, track]);

  const modifiedMessages = useMemo(() => {
    return (messages || []).toReversed();
  }, [messages]);

  const handleSendMessage = async () => {
    try {
      messagesContainer.current?.scrollTo({
        top: messagesContainer.current.scrollHeight,
        behavior: "instant",
      });

      setIsLoading(true);
      setLatestUserMessage(userMessage);
      const response = await generateAIResponseForMentorship(
        mentorshipId,
        userMessage!,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder("utf-8");

      const read = () => {
        reader?.read().then(({ done, value }) => {
          if (done) {
            // Stream completed
            refetch().then(() => {
              setLatestAIResponse(null);
              setLatestUserMessage(null);
            });
            return;
          }
          const chunk = decoder.decode(value);
          setLatestAIResponse((prev) => {
            if (!prev) return chunk;
            return prev + chunk;
          }); // Append the new chunk to the existing text
          read();
        });
      };

      read();
      setUserMessage(null);
    } catch (err) {
      console.log("Error", err);
      // setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  console.log("latestAIResponse", latestAIResponse);
  console.log("latestUserMessage", latestUserMessage);

  return (
    <div className="absolute bottom-20 left-[80px] z-[51] inline-flex h-[640px] w-[460px] flex-col items-center justify-start rounded-lg bg-white shadow-lg">
      <div className="inline-flex w-full items-center justify-start gap-6 px-4 py-4">
        <div className="flex h-6 w-6 items-center justify-center py-1 opacity-0" />
        <div className="flex h-5 shrink grow basis-0 items-center justify-start gap-2">
          <div className="inline-flex shrink grow basis-0 flex-col items-start justify-center gap-1">
            <div className="self-stretch text-center text-base font-bold leading-tight text-black">
              Help me figure out my next steps
            </div>
          </div>
        </div>
        <button onClick={onClose}>
          <X className="relative h-6 w-6" />
        </button>
      </div>

      <div
        className="flex max-h-full w-full flex-1 flex-col-reverse gap-4 overflow-y-auto py-3 pl-3 pr-2"
        ref={messagesContainer}
      >
        {latestAIResponse && <AssistantMessage message={latestAIResponse} />}
        {latestUserMessage && <UserMessage message={latestUserMessage} />}
        {modifiedMessages.map((message, idx) => {
          if (message.type === "user") {
            return <UserMessage key={idx} message={message.message} />;
          }

          return (
            <AssistantMessage
              key={idx}
              message={message.message}
              handleAddToInput={(text) => {
                addToInput(text);
                track(PostHogEvents.COPILOT_RESPONSE_INSERTED, {
                  mentorship_id: mentorshipId,
                  response_length: text.length,
                });
              }}
              handleCreateNote={(text) => {
                createNote(text);
                track(PostHogEvents.COPILOT_RESPONSE_ADDED_TO_NOTE, {
                  mentorship_id: mentorshipId,
                  response_length: text.length,
                });
              }}
            />
          );
        })}
      </div>

      <div className="inline-flex w-full items-center justify-end gap-4 px-4 py-4">
        <div className="inline-flex shrink grow basis-0 flex-col items-start justify-start gap-2">
          <Input
            placeholder="Is there anything else you'd like to know?"
            className="rounded-md"
            value={userMessage || ""}
            onChange={(e) => setUserMessage(e.target.value)}
            onKeyDown={(event) => {
              // Ctrl + Enter to send message
              if (event.key === "Enter" && event.ctrlKey) {
                handleSendMessage();
              }
              // CMD + Enter to send message
              else if (event.key === "Enter" && event.metaKey) {
                handleSendMessage();
              }
            }}
          />
        </div>
        <Button
          variant="outline"
          onClick={handleSendMessage}
          disabled={isLoading}
        >
          Send
          {isLoading ? (
            <Loader2 className="relative ml-1 h-5 w-5 animate-spin" />
          ) : (
            <Undo2 className="ml-1 h-5 w-5" />
          )}
        </Button>
      </div>
    </div>
  );
}

function UserMessage({ message }: { message: string }) {
  return (
    <div className="inline-flex flex-col items-end justify-start gap-2">
      <div className="flex max-w-[80%] flex-col items-start justify-end gap-1">
        <div className="inline-flex items-start justify-start gap-2 rounded-lg border border-[#e2e2e2] p-2">
          <div className="text-sm font-normal text-[#050505]">{message}</div>
        </div>
      </div>
    </div>
  );
}

function AssistantMessage({
  message,
  handleAddToInput,
  handleCreateNote,
}: {
  message: string;
  handleAddToInput?: (text: string) => void;
  handleCreateNote?: (text: string) => void;
}) {
  return (
    <div className="inline-flex items-center justify-start gap-2">
      <div className="inline-flex shrink grow basis-0 flex-col items-start justify-center gap-2">
        <div className="flex flex-col items-start justify-center gap-1 self-stretch">
          <div className="inline-flex items-center justify-start gap-2 self-stretch rounded-xl bg-[#f2f2f2] p-3">
            <div className="prose shrink grow basis-0 text-sm font-normal leading-tight text-[#050505]">
              <Markdown>{message}</Markdown>
            </div>
          </div>
        </div>
        <div className="inline-flex items-start justify-start gap-3 self-stretch">
          <Button
            className="flex items-center justify-center gap-2 px-2 py-1"
            variant="ghost"
            onClick={() => handleCreateNote?.(message)}
          >
            <div className="text-center text-xs font-normal leading-tight text-black">
              Add to notes
            </div>
            <FilePlus2 className="relative h-5 w-5" />
          </Button>
        </div>
      </div>
      <div className="inline-flex flex-col items-start justify-center gap-3">
        <div className="inline-flex items-center justify-center gap-2 rounded-lg p-2">
          <div className="flex items-center justify-center gap-2">
            <button
              className="flex h-6 w-6 items-center justify-center"
              onClick={() => {
                handleAddToInput?.(message);
              }}
            >
              <ArrowDownRightIcon className="relative flex h-6 w-6 flex-col items-start justify-start" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
